/* HR module — Admin + Sub-Admin only.
 *
 * Tabs:
 *   dashboard      — headline KPIs
 *   attendance     — mark daily attendance
 *   salary         — per-employee salary structure
 *   incentive      — rule builder (slab based)
 *   commission     — rule builder (bank / product / loan_type × rate %)
 *   payouts        — computed month-end payouts + finalize
 *   users          — re-uses existing Admin UsersTab (create / approve)
 *   campaigns      — re-uses existing CampaignsTab
 *   team_perf      — re-uses existing SaPerformancePanel
 *   sa_approvals   — re-uses existing SaApprovalsTab
 *   recruitment    — self-registration pipeline (link to public /register)
 */
import { useEffect, useMemo, useState } from "react";
import {
  BarChart3, CalendarCheck, ClipboardList, Coins, FileText, IndianRupee,
  Megaphone, Percent, PhoneCall, Plane, Sparkles, UserCheck, UserPlus, Users,
} from "lucide-react";
import { http, notify } from "../api";
import { Loading, Modal } from "./common";
import { SaApprovalsTab, CampaignsTab, SaPerformancePanel } from "./SaAdmin";
import { UsersTab } from "./AdminPanel";
import { FaceRegisterModal } from "./AttendanceKiosk";

const TABS = [
  ["dashboard",    "Dashboard",       BarChart3],
  ["employees",    "Employees",       Users],
  ["attendance",   "Attendance",      CalendarCheck],
  ["leaves",       "Leaves",          Plane],
  ["reports",      "Monthly report",  FileText],
  ["salary",       "Salary",          IndianRupee],
  ["incentive",    "Incentives",      Sparkles],
  ["commission",   "Commission",      Percent],
  ["payouts",      "Payouts",         Coins],
  ["users",        "User accounts",   UserCheck],
  ["sa_approvals", "SA approvals",    UserCheck],
  ["campaigns",    "Campaigns",       Megaphone],
  ["team_perf",    "Team performance", PhoneCall],
  ["recruitment",  "Recruitment",     UserPlus],
];

export default function HRPanel({ user, existingUsersTab, existingTeams }) {
  const [tab, setTab] = useState("dashboard");
  const [adminData, setAdminData] = useState(null);

  const loadAdmin = () => http.get("/admin/config")
    .then((r) => setAdminData(r.data))
    .catch((e) => notify.error(e, "Could not load admin config"));

  useEffect(() => { loadAdmin(); }, []);
  return (
    <>
      <div className="page-intro">
        <div>
          <p className="eyebrow">HR / PEOPLE OPERATIONS</p>
          <h1>HR</h1>
          <p className="muted">
            Attendance, Salary, Incentives, Commission, Payouts, User approvals, Campaigns,
            Team performance and Recruitment — one place.
          </p>
        </div>
      </div>

      <div className="admin-tabs">
        {TABS.map(([id, label, Icon]) => (
          <button
            key={id}
            data-testid={`hr-tab-${id}`}
            className={tab === id ? "active" : ""}
            onClick={() => setTab(id)}
          >
            <Icon size={13} /> {label}
          </button>
        ))}
      </div>

      {tab === "dashboard"    && <HRDashboard />}
      {tab === "employees"    && <EmployeeManagementTab />}
      {tab === "attendance"   && <AttendanceTab />}
      {tab === "leaves"       && <LeaveManagementTab />}
      {tab === "reports"      && <MonthlyReportTab />}
      {tab === "salary"       && <SalaryTab />}
      {tab === "incentive"    && <IncentiveTab />}
      {tab === "commission"   && <CommissionTab />}
      {tab === "payouts"      && <PayoutsTab />}
      {tab === "users"        && (adminData
        ? <UsersTab data={adminData} reload={loadAdmin} me={user} />
        : <Loading>Loading users…</Loading>)}
      {tab === "sa_approvals" && <SaApprovalsTab me={user} teams={(adminData?.teams) || existingTeams || []} />}
      {tab === "campaigns"    && <CampaignsTab />}
      {tab === "team_perf"    && <SaPerformancePanel days={30} />}
      {tab === "recruitment"  && <RecruitmentTab />}
    </>
  );
}

/* -------------------------------------------------------------- */
function HRDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => {
    http.get("/hr/dashboard").then((r) => setD(r.data)).catch((e) => notify.error(e, "Could not load HR overview"));
  }, []);
  if (!d) return <Loading>Loading HR overview…</Loading>;
  const tiles = [
    ["Total employees",   d.total_employees,     Users],
    ["Present today",     d.present_today,       CalendarCheck],
    ["Half-day today",    d.half_day_today,      CalendarCheck],
    ["On leave today",    d.leave_today,         CalendarCheck],
    ["Unmarked today",    d.unmarked_today,      ClipboardList],
    ["Pending recruits",  d.pending_recruitment, UserPlus],
  ];
  return (
    <section className="panel" data-testid="hr-dashboard">
      <div className="section-title">
        <h3>Overview · {d.today}</h3>
      </div>
      <div className="kpi-row" style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
        {tiles.map(([label, value, Icon]) => (
          <div key={label} data-testid={`hr-kpi-${label}`} style={{ padding: 14, borderRadius: 10, border: "1px solid #e6e6e6", background: "#fff" }}>
            <p className="eyebrow" style={{ margin: 0, display: "flex", alignItems: "center", gap: 6 }}>
              <Icon size={12} /> {label}
            </p>
            <strong style={{ fontSize: 26 }}>{value}</strong>
          </div>
        ))}
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- */
function useHREmployees() {
  const [list, setList] = useState(null);
  useEffect(() => {
    http.get("/hr/employees").then((r) => setList(r.data.employees || [])).catch((e) => notify.error(e, "Could not load employees"));
  }, []);
  return list;
}

function todayISO() { return new Date().toISOString().slice(0, 10); }
function ymNow()    { return new Date().toISOString().slice(0, 7); }

/* -------------------------------------------------------------- */
function AttendanceTab() {
  const employees = useHREmployees();
  const [date, setDate] = useState(todayISO());
  const [month, setMonth] = useState(ymNow());
  const [entries, setEntries] = useState({}); // user_id -> status
  const [dayRows, setDayRows] = useState({}); // user_id -> full row for the day
  const [monthEntries, setMonthEntries] = useState([]); // full month data (for face-attendance list)
  const [summary, setSummary] = useState({});
  const [busy, setBusy] = useState(false);
  const [showFace, setShowFace] = useState(false);

  const loadMonth = () => {
    http.get(`/hr/attendance?month=${month}`).then((r) => {
      setSummary(r.data.summary || {});
      setMonthEntries(r.data.entries || []);
      // Prefill today's status + surface login_time/logout_time/working_hours.
      const map = {};
      const rows = {};
      (r.data.entries || []).filter((e) => e.date === date).forEach((e) => {
        map[e.user_id] = e.status;
        rows[e.user_id] = e;
      });
      setEntries(map);
      setDayRows(rows);
    }).catch(() => {});
  };
  useEffect(() => { loadMonth(); /* eslint-disable-next-line */ }, [month, date]);

  if (!employees) return <Loading>Loading attendance…</Loading>;

  const setStatus = (uid, status) => setEntries((prev) => ({ ...prev, [uid]: status }));

  const save = async () => {
    const payload = Object.entries(entries).map(([uid, status]) => ({ user_id: uid, date, status }));
    if (!payload.length) { notify.error("Mark at least one employee"); return; }
    setBusy(true);
    try {
      await http.post("/hr/attendance", { entries: payload });
      notify.success(`Attendance saved for ${payload.length} employee(s)`);
      loadMonth();
    } catch (e) { notify.error(e, "Could not save attendance"); }
    finally { setBusy(false); }
  };

  const bulk = (status) => {
    const next = {};
    employees.forEach((u) => { next[u.id] = status; });
    setEntries(next);
  };

  const CHIPS = [
    ["present",  "P", "#16a34a"],
    ["half_day", "H", "#f59e0b"],
    ["leave",    "L", "#2563eb"],
    ["absent",   "A", "#dc2626"],
  ];

  return (
    <section className="panel" data-testid="hr-attendance">
      <div className="section-title" style={{ flexWrap: "wrap", gap: 10 }}>
        <h3>Mark attendance</h3>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <label>Date <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="hr-att-date" /></label>
          <label>Month <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="hr-att-month" /></label>
          <button className="outline-button compact" onClick={() => bulk("present")}>Mark all P</button>
          <button className="primary-button compact" onClick={save} disabled={busy} data-testid="hr-att-save">
            {busy ? "Saving…" : "Save attendance"}
          </button>
        </div>
      </div>

      <table className="records-table" data-testid="hr-attendance-table">
        <thead>
          <tr>
            <th>Employee</th><th>Role</th>
            <th>Mark ({date})</th>
            <th>Source</th><th>Login</th><th>Logout</th><th>Hours</th><th>Late</th>
            <th>P</th><th>H</th><th>L</th><th>A</th><th>Total hrs</th>
          </tr>
        </thead>
        <tbody>
          {employees.map((u) => {
            const s = summary[u.id] || {};
            const cur = entries[u.id];
            const dr = dayRows[u.id] || {};
            const fmt = (iso) => iso ? String(iso).slice(11, 16) : "—";
            return (
              <tr key={u.id} data-testid={`hr-att-row-${u.id}`}>
                <td><strong>{u.name}</strong><small className="muted"> · {u.employee_code || u.email}</small></td>
                <td>{u.role}</td>
                <td>
                  <div style={{ display: "flex", gap: 6 }}>
                    {CHIPS.map(([status, letter, color]) => (
                      <button
                        key={status}
                        onClick={() => setStatus(u.id, status)}
                        data-testid={`hr-att-${status}-${u.id}`}
                        title={status}
                        style={{
                          width: 28, height: 28, borderRadius: 6,
                          border: cur === status ? `2px solid ${color}` : "1px solid #ddd",
                          background: cur === status ? color : "#fff",
                          color: cur === status ? "#fff" : "#333",
                          fontWeight: 700, cursor: "pointer",
                        }}
                      >{letter}</button>
                    ))}
                  </div>
                </td>
                <td>{dr.source ? <span className={`chip ${dr.source === "Face Scan" ? "chip-approved" : "chip-pending"}`}>{dr.source}</span> : "—"}</td>
                <td>{fmt(dr.login_time)}</td>
                <td>{fmt(dr.logout_time)}</td>
                <td>{dr.working_hours ? `${dr.working_hours}h` : "—"}</td>
                <td>{dr.late_login ? <span style={{ color: "#dc2626", fontWeight: 700 }}>YES</span> : "—"}</td>
                <td>{s.present || 0}</td>
                <td>{s.half_day || 0}</td>
                <td>{s.leave || 0}</td>
                <td>{s.absent || 0}</td>
                <td>{s.total_hours ? `${s.total_hours}h` : "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* FACE ATTENDANCE — HR/Admin visibility panel (reuses same attendance data) */}
      <div style={{ marginTop: 20, borderTop: "1px solid #e5e7eb", paddingTop: 16 }} data-testid="hr-face-section">
        <button
          className="outline-button compact"
          onClick={() => setShowFace((v) => !v)}
          data-testid="hr-face-toggle"
        >
          {showFace ? "▾" : "▸"} Face Attendance
        </button>
        {showFace && (
          <FaceAttendancePanel employees={employees} monthEntries={monthEntries} month={month} />
        )}
      </div>
    </section>
  );
}

/* Face Attendance — read-only HR view over existing data */
function FaceAttendancePanel({ employees, monthEntries, month }) {
  const [view, setView] = useState("registry"); // registry | records
  const faceOnly = (monthEntries || []).filter((r) => r.source === "Face Scan");
  const fmt = (iso) => iso ? String(iso).slice(11, 16) : "—";
  const nameOf = (uid) => (employees || []).find((e) => e.id === uid)?.name || uid;
  const empBadge = (uid) => (employees || []).find((e) => e.id === uid)?.employee_code || "";
  const registered = (employees || []).filter((e) => e.face_registered).length;

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
        <div className="muted">
          Registered: <strong>{registered}</strong> / {(employees || []).length}
          {" · "}Face-scan records in {month}: <strong>{faceOnly.length}</strong>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button
            className={view === "registry" ? "primary-button compact" : "outline-button compact"}
            onClick={() => setView("registry")}
            data-testid="hr-face-view-registry"
          >Registration Status</button>
          <button
            className={view === "records" ? "primary-button compact" : "outline-button compact"}
            onClick={() => setView("records")}
            data-testid="hr-face-view-records"
          >Face Attendance Records</button>
        </div>
      </div>

      {view === "registry" && (
        <table className="records-table" data-testid="hr-face-registry-table">
          <thead><tr>
            <th>Emp ID</th><th>Name</th><th>Department</th><th>Designation</th>
            <th>Branch</th><th>Face Status</th><th>Registered on</th>
          </tr></thead>
          <tbody>
            {(employees || []).map((e) => (
              <tr key={e.id} data-testid={`hr-face-reg-row-${e.id}`}>
                <td><strong>{e.employee_code || "—"}</strong></td>
                <td>{e.name}<div className="muted" style={{ fontSize: 11 }}>{e.role}</div></td>
                <td>{e.department || "—"}</td>
                <td>{e.designation || "—"}</td>
                <td>{e.branch || "—"}</td>
                <td>
                  {e.face_registered
                    ? <span className="chip chip-approved">Registered</span>
                    : <span className="chip chip-pending">Not registered</span>}
                </td>
                <td>{e.face_registered_at ? String(e.face_registered_at).slice(0, 10) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {view === "records" && (
        faceOnly.length === 0 ? (
          <div className="empty">No face-scan attendance records in {month} yet.</div>
        ) : (
          <table className="records-table" data-testid="hr-face-records-table">
            <thead><tr>
              <th>Date</th><th>Employee</th><th>Login</th><th>Logout</th>
              <th>Hours</th><th>Late</th><th>Source</th>
            </tr></thead>
            <tbody>
              {faceOnly.map((r, i) => (
                <tr key={`${r.user_id}-${r.date}-${i}`} data-testid={`hr-face-record-row-${r.user_id}-${r.date}`}>
                  <td>{r.date}</td>
                  <td><strong>{nameOf(r.user_id)}</strong><small className="muted"> · {empBadge(r.user_id)}</small></td>
                  <td>{fmt(r.login_time)}</td>
                  <td>{fmt(r.logout_time)}</td>
                  <td>{r.working_hours ? `${r.working_hours}h` : "—"}</td>
                  <td>{r.late_login ? <span style={{ color: "#dc2626", fontWeight: 700 }}>YES</span> : "—"}</td>
                  <td><span className="chip chip-approved">{r.source}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}
    </div>
  );
}

/* -------------------------------------------------------------- */
function SalaryTab() {
  const employees = useHREmployees();
  const [rows, setRows] = useState({});
  const [busy, setBusy] = useState("");

  const load = () => http.get("/hr/salary-structures").then((r) => {
    const map = {};
    (r.data.structures || []).forEach((s) => { map[s.user_id] = s; });
    setRows(map);
  }).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (!employees) return <Loading>Loading salary structures…</Loading>;

  const change = (uid, field, val) => setRows((prev) => ({
    ...prev, [uid]: { ...(prev[uid] || {}), [field]: val },
  }));

  const save = async (uid) => {
    const r = rows[uid] || {};
    setBusy(uid);
    try {
      await http.post("/hr/salary-structures", {
        user_id: uid,
        base: Number(r.base) || 0,
        hra: Number(r.hra) || 0,
        allowances: Number(r.allowances) || 0,
        deductions: Number(r.deductions) || 0,
        effective_from: r.effective_from || todayISO(),
      });
      notify.success("Salary saved");
      load();
    } catch (e) { notify.error(e, "Could not save salary"); }
    finally { setBusy(""); }
  };

  return (
    <section className="panel" data-testid="hr-salary">
      <div className="section-title"><h3>Salary structures</h3></div>
      <table className="records-table">
        <thead><tr>
          <th>Employee</th><th>Base</th><th>HRA</th><th>Allowances</th><th>Deductions</th><th>Effective from</th><th></th>
        </tr></thead>
        <tbody>
          {employees.map((u) => {
            const r = rows[u.id] || {};
            return (
              <tr key={u.id} data-testid={`hr-salary-row-${u.id}`}>
                <td><strong>{u.name}</strong><small className="muted"> · {u.employee_code || u.email}</small></td>
                {["base", "hra", "allowances", "deductions"].map((f) => (
                  <td key={f}><input type="number" min="0" style={{ width: 100 }} value={r[f] ?? ""} onChange={(e) => change(u.id, f, e.target.value)} data-testid={`hr-salary-${f}-${u.id}`} /></td>
                ))}
                <td><input type="date" value={r.effective_from || ""} onChange={(e) => change(u.id, "effective_from", e.target.value)} /></td>
                <td>
                  <button className="primary-button compact" onClick={() => save(u.id)} disabled={busy === u.id} data-testid={`hr-salary-save-${u.id}`}>
                    {busy === u.id ? "…" : "Save"}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

/* -------------------------------------------------------------- */
const INC_METRICS = [
  ["disbursed_count",  "# of disbursed loans"],
  ["disbursed_amount", "Total disbursed amount"],
  ["leads_created",    "Leads created"],
  ["leads_disbursed",  "Leads disbursed"],
];

function IncentiveTab() {
  const [rules, setRules] = useState(null);
  const [draft, setDraft] = useState(null);

  const load = () => http.get("/hr/incentive-rules").then((r) => setRules(r.data.rules || [])).catch((e) => notify.error(e, "Could not load rules"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this incentive rule?")) return;
    try { await http.delete(`/hr/incentive-rules/${id}`); notify.success("Deleted"); load(); }
    catch (e) { notify.error(e, "Could not delete"); }
  };

  if (rules === null) return <Loading>Loading incentives…</Loading>;

  return (
    <section className="panel" data-testid="hr-incentive">
      <div className="section-title">
        <h3>Incentive rules</h3>
        <button className="primary-button" onClick={() => setDraft({ name: "", metric: "disbursed_count", slabs: [{ min: 0, max: null, amount: 0 }], active: true })} data-testid="hr-incentive-new">+ New rule</button>
      </div>

      {rules.length === 0 ? (
        <div className="empty">No incentive rules yet. Create your first slab-based rule.</div>
      ) : (
        <table className="records-table">
          <thead><tr><th>Name</th><th>Metric</th><th>Slabs</th><th>Active</th><th></th></tr></thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.rule_id} data-testid={`hr-incentive-row-${r.rule_id}`}>
                <td><strong>{r.name}</strong></td>
                <td>{(INC_METRICS.find(([m]) => m === r.metric) || [])[1] || r.metric}</td>
                <td>{(r.slabs || []).map((s, i) => <div key={i} className="muted">{s.min}{s.max != null ? `–${s.max}` : "+"} → ₹{s.amount}</div>)}</td>
                <td>{r.active ? "Yes" : "No"}</td>
                <td>
                  <button className="outline-button compact" onClick={() => setDraft({ ...r })} data-testid={`hr-incentive-edit-${r.rule_id}`}>Edit</button>{" "}
                  <button className="outline-button compact danger-button" onClick={() => remove(r.rule_id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {draft && <RuleEditor
        draft={draft}
        setDraft={setDraft}
        metrics={INC_METRICS}
        endpoint="/hr/incentive-rules"
        onSaved={() => { setDraft(null); load(); }}
      />}
    </section>
  );
}

function RuleEditor({ draft, setDraft, metrics, endpoint, onSaved }) {
  const [busy, setBusy] = useState(false);
  const isEdit = !!draft.rule_id;

  const save = async () => {
    if (!draft.name.trim()) { notify.error("Rule name is required"); return; }
    setBusy(true);
    try {
      if (isEdit) await http.patch(`${endpoint}/${draft.rule_id}`, draft);
      else        await http.post(endpoint, draft);
      notify.success("Saved");
      onSaved();
    } catch (e) { notify.error(e, "Could not save"); }
    finally { setBusy(false); }
  };

  const updSlab = (i, patch) => setDraft({ ...draft, slabs: draft.slabs.map((s, j) => j === i ? { ...s, ...patch } : s) });

  return (
    <div style={{ marginTop: 16, padding: 14, border: "1px solid #d1d5db", borderRadius: 10, background: "#fafafa" }}>
      <h4>{isEdit ? "Edit rule" : "New rule"}</h4>
      <div className="form-grid">
        <label>Name<input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="hr-rule-name" /></label>
        <label>Metric
          <select value={draft.metric} onChange={(e) => setDraft({ ...draft, metric: e.target.value })} data-testid="hr-rule-metric">
            {metrics.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label>Active
          <select value={draft.active ? "yes" : "no"} onChange={(e) => setDraft({ ...draft, active: e.target.value === "yes" })}>
            <option value="yes">Yes</option><option value="no">No</option>
          </select>
        </label>
      </div>
      <div style={{ marginTop: 10 }}>
        <p className="eyebrow">SLABS</p>
        {draft.slabs.map((s, i) => (
          <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
            <input type="number" placeholder="min" value={s.min ?? ""} onChange={(e) => updSlab(i, { min: Number(e.target.value) || 0 })} style={{ width: 100 }} data-testid={`hr-slab-min-${i}`} />
            <span>–</span>
            <input type="number" placeholder="max (blank = ∞)" value={s.max ?? ""} onChange={(e) => updSlab(i, { max: e.target.value === "" ? null : Number(e.target.value) })} style={{ width: 120 }} data-testid={`hr-slab-max-${i}`} />
            <span>→ ₹</span>
            <input type="number" placeholder="amount" value={s.amount ?? ""} onChange={(e) => updSlab(i, { amount: Number(e.target.value) || 0 })} style={{ width: 120 }} data-testid={`hr-slab-amt-${i}`} />
            <button className="outline-button compact danger-button" onClick={() => setDraft({ ...draft, slabs: draft.slabs.filter((_, j) => j !== i) })}>×</button>
          </div>
        ))}
        <button className="outline-button compact" onClick={() => setDraft({ ...draft, slabs: [...draft.slabs, { min: 0, max: null, amount: 0 }] })} data-testid="hr-slab-add">+ Slab</button>
      </div>
      <div className="form-actions" style={{ marginTop: 12 }}>
        <button className="outline-button" onClick={() => onSaved()}>Cancel</button>
        <button className="primary-button" onClick={save} disabled={busy} data-testid="hr-rule-save">{busy ? "Saving…" : "Save rule"}</button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- */
function CommissionTab() {
  const [rules, setRules] = useState(null);
  const [draft, setDraft] = useState(null);
  const [config, setConfig] = useState({ banks: [], products: [] });

  useEffect(() => {
    http.get("/admin/config").then((r) => setConfig({ banks: r.data.banks || [], products: r.data.products || [] })).catch(() => {});
    load();
  }, []);
  const load = () => http.get("/hr/commission-rules").then((r) => setRules(r.data.rules || [])).catch((e) => notify.error(e, "Could not load"));

  const remove = async (id) => {
    if (!window.confirm("Delete this commission rule?")) return;
    try { await http.delete(`/hr/commission-rules/${id}`); notify.success("Deleted"); load(); }
    catch (e) { notify.error(e, "Could not delete"); }
  };

  if (rules === null) return <Loading>Loading commissions…</Loading>;

  const bankName = (id) => (config.banks.find((b) => b.bank_id === id) || {}).name || id || "Any";
  const prodName = (id) => (config.products.find((p) => p.product_id === id) || {}).name || id || "Any";

  return (
    <section className="panel" data-testid="hr-commission">
      <div className="section-title">
        <h3>Commission rules</h3>
        <button className="primary-button" onClick={() => setDraft({ name: "", bank_id: "", product_id: "", loan_type: "", rate_pct: 0, active: true })} data-testid="hr-commission-new">+ New rule</button>
      </div>
      {rules.length === 0 ? (
        <div className="empty">No commission rules. Add rules per bank / product / loan type — most specific match wins.</div>
      ) : (
        <table className="records-table">
          <thead><tr><th>Name</th><th>Bank</th><th>Product</th><th>Loan type</th><th>Rate %</th><th>Active</th><th></th></tr></thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.rule_id} data-testid={`hr-commission-row-${r.rule_id}`}>
                <td><strong>{r.name}</strong></td>
                <td>{r.bank_id ? bankName(r.bank_id) : "Any"}</td>
                <td>{r.product_id ? prodName(r.product_id) : "Any"}</td>
                <td>{r.loan_type || "Any"}</td>
                <td>{r.rate_pct}%</td>
                <td>{r.active ? "Yes" : "No"}</td>
                <td>
                  <button className="outline-button compact" onClick={() => setDraft({ ...r })}>Edit</button>{" "}
                  <button className="outline-button compact danger-button" onClick={() => remove(r.rule_id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {draft && <CommissionEditor draft={draft} setDraft={setDraft} config={config} onSaved={() => { setDraft(null); load(); }} />}
    </section>
  );
}

function CommissionEditor({ draft, setDraft, config, onSaved }) {
  const [busy, setBusy] = useState(false);
  const isEdit = !!draft.rule_id;
  const save = async () => {
    if (!draft.name.trim()) { notify.error("Rule name is required"); return; }
    setBusy(true);
    try {
      if (isEdit) await http.patch(`/hr/commission-rules/${draft.rule_id}`, draft);
      else        await http.post("/hr/commission-rules", draft);
      notify.success("Saved");
      onSaved();
    } catch (e) { notify.error(e, "Could not save"); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ marginTop: 16, padding: 14, border: "1px solid #d1d5db", borderRadius: 10, background: "#fafafa" }}>
      <h4>{isEdit ? "Edit rule" : "New rule"}</h4>
      <div className="form-grid">
        <label>Name<input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="hr-commission-name" /></label>
        <label>Bank
          <select value={draft.bank_id || ""} onChange={(e) => setDraft({ ...draft, bank_id: e.target.value })}>
            <option value="">Any bank</option>
            {config.banks.map((b) => <option key={b.bank_id} value={b.bank_id}>{b.name}</option>)}
          </select>
        </label>
        <label>Product
          <select value={draft.product_id || ""} onChange={(e) => setDraft({ ...draft, product_id: e.target.value })}>
            <option value="">Any product</option>
            {config.products.map((p) => <option key={p.product_id} value={p.product_id}>{p.name}</option>)}
          </select>
        </label>
        <label>Loan type<input value={draft.loan_type || ""} placeholder="e.g. Personal Loan" onChange={(e) => setDraft({ ...draft, loan_type: e.target.value })} /></label>
        <label>Rate % <input type="number" step="0.01" value={draft.rate_pct ?? 0} onChange={(e) => setDraft({ ...draft, rate_pct: Number(e.target.value) })} data-testid="hr-commission-rate" /></label>
        <label>Active
          <select value={draft.active ? "yes" : "no"} onChange={(e) => setDraft({ ...draft, active: e.target.value === "yes" })}>
            <option value="yes">Yes</option><option value="no">No</option>
          </select>
        </label>
      </div>
      <div className="form-actions" style={{ marginTop: 12 }}>
        <button className="outline-button" onClick={() => onSaved()}>Cancel</button>
        <button className="primary-button" onClick={save} disabled={busy} data-testid="hr-commission-save">{busy ? "Saving…" : "Save"}</button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- */
function PayoutsTab() {
  const [month, setMonth] = useState(ymNow());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setData(null);
    http.get(`/hr/payouts?month=${month}`).then((r) => setData(r.data)).catch((e) => notify.error(e, "Could not compute payouts"));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const finalize = async () => {
    if (!window.confirm(`Finalize payouts for ${month}? A snapshot will be saved.`)) return;
    setBusy(true);
    try {
      await http.post("/hr/payouts/finalize", { month });
      notify.success("Payouts finalized");
      load();
    } catch (e) { notify.error(e, "Could not finalize"); }
    finally { setBusy(false); }
  };

  const totalPayable = useMemo(() =>
    (data?.rows || []).reduce((s, r) => s + (r.total || 0), 0), [data]);

  if (!data) return <Loading>Computing payouts…</Loading>;

  return (
    <section className="panel" data-testid="hr-payouts">
      <div className="section-title" style={{ flexWrap: "wrap", gap: 10 }}>
        <h3>Month-end payouts · {month}</h3>
        <div style={{ display: "flex", gap: 8 }}>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="hr-payouts-month" />
          <button className="outline-button" onClick={load}>Recompute</button>
          <button className="primary-button" onClick={finalize} disabled={busy} data-testid="hr-payouts-finalize">
            {busy ? "Saving…" : "Finalize month"}
          </button>
        </div>
      </div>
      <div className="muted" style={{ marginBottom: 8 }}>
        Working days: <strong>{data.working_days}</strong> (Sundays excluded) · Total payable:{" "}
        <strong>₹{totalPayable.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</strong>
      </div>
      <table className="records-table">
        <thead><tr>
          <th>Employee</th><th>Days</th><th>Base (pro-rata)</th><th>HRA + Allow</th>
          <th>Incentive</th><th>Commission</th><th>Deductions</th><th>Total</th><th>Disbursed</th>
        </tr></thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={r.user_id} data-testid={`hr-payout-row-${r.user_id}`}>
              <td><strong>{r.name}</strong><small className="muted"> · {r.employee_code || r.role}</small></td>
              <td>{r.attendance_days} / {r.working_days}</td>
              <td>₹{r.base_pro_rata.toLocaleString("en-IN")}</td>
              <td>₹{(r.hra + r.allowances).toLocaleString("en-IN")}</td>
              <td>₹{r.incentive_total.toLocaleString("en-IN")}</td>
              <td>₹{r.commission_total.toLocaleString("en-IN")}</td>
              <td>₹{r.deductions.toLocaleString("en-IN")}</td>
              <td><strong>₹{r.total.toLocaleString("en-IN")}</strong>
                  {r.finalized && <div style={{ fontSize: 11, color: "#16a34a" }}>Finalized</div>}</td>
              <td>{r.disbursed_count} · ₹{r.disbursed_amount.toLocaleString("en-IN")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/* -------------------------------------------------------------- */
function RecruitmentTab() {
  const [status, setStatus] = useState("pending");
  const [list, setList] = useState(null);
  useEffect(() => {
    setList(null);
    http.get(`/hr/recruitment?status=${status}`).then((r) => setList(r.data.candidates || [])).catch((e) => notify.error(e, "Could not load"));
  }, [status]);

  const publicUrl = `${window.location.origin}/register`;
  const copy = () => { navigator.clipboard.writeText(publicUrl); notify.success("Public sign-up link copied"); };

  if (list === null) return <Loading>Loading recruitment pipeline…</Loading>;

  return (
    <section className="panel" data-testid="hr-recruitment">
      <div className="section-title" style={{ flexWrap: "wrap", gap: 10 }}>
        <h3>Recruitment</h3>
        <div style={{ display: "flex", gap: 8 }}>
          {["pending", "correction_required", "approved", "rejected"].map((s) => (
            <button key={s} className={status === s ? "active admin-tabs-item" : "outline-button compact"} onClick={() => setStatus(s)} data-testid={`hr-recruit-tab-${s}`}>{s.replace("_", " ")}</button>
          ))}
        </div>
      </div>
      <div className="muted" style={{ marginBottom: 8 }}>
        Public sign-up link: <a href={publicUrl} target="_blank" rel="noreferrer">{publicUrl}</a>{" "}
        <button className="outline-button compact" onClick={copy} data-testid="hr-recruit-copy">Copy</button>{" "}
        <span>· Approvals happen under <strong>SA approvals</strong> tab.</span>
      </div>
      {list.length === 0 ? (
        <div className="empty">No candidates in this bucket.</div>
      ) : (
        <table className="records-table">
          <thead><tr><th>Candidate</th><th>Mobile</th><th>Team</th><th>Applied</th><th>Status</th></tr></thead>
          <tbody>
            {list.map((c) => (
              <tr key={c.employee_id} data-testid={`hr-recruit-row-${c.employee_id}`}>
                <td><strong>{c.name}</strong><small className="muted"> · {c.email}</small></td>
                <td>{c.mobile || "—"}</td>
                <td>{c.team_name || "Unassigned"}</td>
                <td>{(c.created_at || "").slice(0, 10)}</td>
                <td><span className={`chip chip-${c.approval_status}`}>{c.approval_status.replace("_", " ")}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}


/* -------------------------------------------------------------- */
/*  EMPLOYEE MANAGEMENT (new HR fields on top of existing users)  */
/* -------------------------------------------------------------- */
function EmployeeManagementTab() {
  const [list, setList] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = () => {
    setList(null);
    http.get("/hr/employees").then((r) => setList(r.data.employees || []))
      .catch((e) => notify.error(e, "Could not load employees"));
  };
  useEffect(() => { load(); }, []);

  if (list === null) return <Loading>Loading employees…</Loading>;

  return (
    <section className="panel" data-testid="hr-employees">
      <div className="section-title">
        <h3>Employee directory · {list.length}</h3>
        <span className="muted">All new-user creation happens in <strong>User accounts</strong> tab.</span>
      </div>

      <table className="records-table" data-testid="hr-employee-table">
        <thead><tr>
          <th>Emp ID</th><th>Name</th><th>Mobile</th><th>Email</th>
          <th>Department</th><th>Designation</th><th>Branch</th>
          <th>Joining</th><th>Reports to</th><th>Docs</th><th>Status</th><th></th>
        </tr></thead>
        <tbody>
          {list.map((e) => (
            <tr key={e.id} data-testid={`hr-emp-row-${e.id}`}>
              <td><strong>{e.employee_code || "—"}</strong></td>
              <td>{e.name}<div className="muted" style={{ fontSize: 11 }}>{e.role}</div></td>
              <td>{e.mobile || "—"}</td>
              <td className="muted">{e.email || "—"}</td>
              <td>{e.department || "—"}</td>
              <td>{e.designation || "—"}</td>
              <td>{e.branch || "—"}</td>
              <td>{e.joining_date || "—"}</td>
              <td>{e.reporting_manager_name || "—"}</td>
              <td>{(e.documents || []).length}</td>
              <td>
                <span className={`chip ${e.active ? "chip-approved" : "chip-rejected"}`}>
                  {e.active ? "Active" : "Inactive"}
                </span>
              </td>
              <td>
                <button className="outline-button compact" onClick={() => setSelected(e)}
                        data-testid={`hr-emp-edit-${e.id}`}>Edit</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {selected && (
        <EmployeeEditModal
          employee={selected}
          allEmployees={list}
          onClose={() => setSelected(null)}
          onSaved={() => { setSelected(null); load(); }}
        />
      )}
    </section>
  );
}

function EmployeeEditModal({ employee, allEmployees, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: employee.name || "",
    mobile: employee.mobile || "",
    email: employee.email || "",
    department: employee.department || "",
    designation: employee.designation || "",
    joining_date: employee.joining_date || "",
    branch: employee.branch || "",
    reporting_manager_id: employee.reporting_manager_id || "",
    active: !!employee.active,
  });
  const [busy, setBusy] = useState(false);
  const [faceOpen, setFaceOpen] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await http.patch(`/admin/users/${employee.id}`, form);
      notify.success("Employee updated");
      onSaved();
    } catch (e) { notify.error(e, "Could not update employee"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={`Edit — ${employee.name}`} onClose={onClose}>
      <div className="record-form" style={{ padding: 0 }}>
        <div className="form-grid">
          <label>Employee ID<input value={employee.employee_code || "—"} disabled /></label>
          <label>Name<input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="hr-emp-name" /></label>
          <label>Mobile<input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="hr-emp-mobile" /></label>
          <label>Email<input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="hr-emp-email" /></label>
          <label>Department<input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} placeholder="e.g. Sales / HR / Ops" data-testid="hr-emp-department" /></label>
          <label>Designation<input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} placeholder="e.g. Sales Executive" data-testid="hr-emp-designation" /></label>
          <label>Branch<input value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value })} placeholder="e.g. Mumbai HQ" data-testid="hr-emp-branch" /></label>
          <label>Joining date<input type="date" value={form.joining_date} onChange={(e) => setForm({ ...form, joining_date: e.target.value })} data-testid="hr-emp-joining" /></label>
          <label>Reporting manager
            <select value={form.reporting_manager_id} onChange={(e) => setForm({ ...form, reporting_manager_id: e.target.value })} data-testid="hr-emp-manager">
              <option value="">— none —</option>
              {allEmployees.filter((x) => x.id !== employee.id).map((x) => (
                <option key={x.id} value={x.id}>{x.name} · {x.role}</option>
              ))}
            </select>
          </label>
          <label>Status
            <select value={form.active ? "yes" : "no"} onChange={(e) => setForm({ ...form, active: e.target.value === "yes" })} data-testid="hr-emp-active">
              <option value="yes">Active</option>
              <option value="no">Inactive</option>
            </select>
          </label>
        </div>

        {employee.documents && employee.documents.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <p className="eyebrow">DOCUMENTS ({employee.documents.length})</p>
            <ul style={{ paddingLeft: 18 }}>
              {employee.documents.map((d, i) => {
                const href = d.url && d.url.startsWith("/api/")
                  ? `${process.env.REACT_APP_BACKEND_URL}${d.url}` : d.url;
                return (
                  <li key={i}>
                    <a href={href} target="_blank" rel="noreferrer" style={{ color: "#2563eb" }}>
                      {d.type || d.filename || "Document"}
                    </a>
                    <span className="muted"> · {d.content_type || ""}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <div className="form-actions">
          <button className="outline-button" onClick={onClose}>Cancel</button>
          <button className="outline-button" onClick={() => setFaceOpen(true)} data-testid={`hr-emp-face-${employee.id}`}>
            Register / Update Face
          </button>
          <button className="primary-button" onClick={save} disabled={busy} data-testid="hr-emp-save">
            {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
      {faceOpen && (
        <FaceRegisterModal
          employee={employee}
          onClose={() => setFaceOpen(false)}
          onSaved={() => { setFaceOpen(false); onSaved && onSaved(); }}
        />
      )}
    </Modal>
  );
}


/* -------------------------------------------------------------- */
/*  LEAVE MANAGEMENT                                              */
/* -------------------------------------------------------------- */
function LeaveManagementTab() {
  const employees = useHREmployees();
  const [status, setStatus] = useState("all");
  const [list, setList] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => {
    setList(null);
    http.get(`/hr/leaves?status=${status}`).then((r) => setList(r.data.leaves || []))
      .catch((e) => notify.error(e, "Could not load leaves"));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  if (!employees || list === null) return <Loading>Loading leaves…</Loading>;

  const decide = async (leave_id, decision) => {
    if (decision === "rejected" && !window.confirm("Reject this leave?")) return;
    try {
      await http.patch(`/hr/leaves/${leave_id}`, { status: decision });
      notify.success(decision === "approved" ? "Leave approved · attendance marked" : "Leave rejected");
      load();
    } catch (e) { notify.error(e, "Could not save decision"); }
  };

  const del = async (leave_id) => {
    if (!window.confirm("Delete this leave request?")) return;
    try { await http.delete(`/hr/leaves/${leave_id}`); notify.success("Deleted"); load(); }
    catch (e) { notify.error(e, "Could not delete"); }
  };

  return (
    <section className="panel" data-testid="hr-leaves">
      <div className="section-title" style={{ flexWrap: "wrap", gap: 10 }}>
        <h3>Leave management</h3>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="hr-leaves-filter">
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          <button className="primary-button" onClick={() => setCreating(true)} data-testid="hr-leaves-new">+ New leave</button>
        </div>
      </div>

      {list.length === 0 ? (
        <div className="empty">No leave requests in this bucket.</div>
      ) : (
        <table className="records-table">
          <thead><tr>
            <th>Employee</th><th>Type</th><th>From</th><th>To</th><th>Days</th><th>Reason</th><th>Status</th><th></th>
          </tr></thead>
          <tbody>
            {list.map((lv) => (
              <tr key={lv.leave_id} data-testid={`hr-leave-row-${lv.leave_id}`}>
                <td><strong>{lv.employee_name || lv.user_id}</strong></td>
                <td>{lv.leave_type}</td>
                <td>{lv.from_date}</td>
                <td>{lv.to_date}</td>
                <td>{lv.days}</td>
                <td className="muted">{lv.reason || "—"}</td>
                <td><span className={`chip chip-${lv.status}`}>{lv.status}</span></td>
                <td>
                  {lv.status === "pending" && (
                    <>
                      <button className="primary-button compact" onClick={() => decide(lv.leave_id, "approved")} data-testid={`hr-leave-approve-${lv.leave_id}`}>Approve</button>{" "}
                      <button className="outline-button compact danger-button" onClick={() => decide(lv.leave_id, "rejected")}>Reject</button>{" "}
                    </>
                  )}
                  <button className="outline-button compact" onClick={() => del(lv.leave_id)}>×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {creating && (
        <LeaveCreateModal employees={employees} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />
      )}
    </section>
  );
}

function LeaveCreateModal({ employees, onClose, onSaved }) {
  const [form, setForm] = useState({
    user_id: employees[0]?.id || "",
    from_date: todayISO(),
    to_date: todayISO(),
    leave_type: "casual",
    reason: "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!form.user_id) { notify.error("Select an employee"); return; }
    setBusy(true);
    try {
      await http.post("/hr/leaves", form);
      notify.success("Leave request created");
      onSaved();
    } catch (e) { notify.error(e, "Could not create leave"); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="New leave request" onClose={onClose}>
      <div className="record-form" style={{ padding: 0 }}>
        <div className="form-grid">
          <label>Employee
            <select value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} data-testid="hr-leave-employee">
              {employees.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.employee_code || u.role}</option>)}
            </select>
          </label>
          <label>Type
            <select value={form.leave_type} onChange={(e) => setForm({ ...form, leave_type: e.target.value })} data-testid="hr-leave-type">
              <option value="casual">Casual</option>
              <option value="sick">Sick</option>
              <option value="earned">Earned</option>
              <option value="unpaid">Unpaid</option>
            </select>
          </label>
          <label>From<input type="date" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} data-testid="hr-leave-from" /></label>
          <label>To<input type="date" value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} data-testid="hr-leave-to" /></label>
        </div>
        <label>Reason
          <textarea rows={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="hr-leave-reason" />
        </label>
        <div className="form-actions">
          <button className="outline-button" onClick={onClose}>Cancel</button>
          <button className="primary-button" onClick={save} disabled={busy} data-testid="hr-leave-save">
            {busy ? "Saving…" : "Create request"}
          </button>
        </div>
      </div>
    </Modal>
  );
}


/* -------------------------------------------------------------- */
/*  MONTHLY ATTENDANCE REPORT                                     */
/* -------------------------------------------------------------- */
function MonthlyReportTab() {
  const employees = useHREmployees();
  const [month, setMonth] = useState(ymNow());
  const [userId, setUserId] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!userId && employees && employees.length) setUserId(employees[0].id);
  }, [employees, userId]);

  const load = () => {
    if (!userId) return;
    setData(null);
    http.get(`/hr/attendance/report?month=${month}&user_id=${userId}`)
      .then((r) => setData(r.data))
      .catch((e) => notify.error(e, "Could not load report"));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, userId]);

  const download = () => {
    if (!data) return;
    const rows = [["Date", "Weekday", "Status", "Login", "Logout", "Working hours", "Late", "Note"]];
    data.days.forEach((d) => rows.push([
      d.date, d.weekday, d.status || "-",
      d.login_time ? d.login_time.slice(11, 16) : "-",
      d.logout_time ? d.logout_time.slice(11, 16) : "-",
      d.working_hours || 0, d.late_login ? "YES" : "-", d.note || "",
    ]));
    const csv = rows.map((r) => r.map((c) => /[",\n]/.test(String(c)) ? `"${String(c).replace(/"/g, '""')}"` : c).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `attendance_${data.employee.employee_code || data.employee.name}_${month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!employees) return <Loading>Loading employees…</Loading>;

  return (
    <section className="panel" data-testid="hr-report">
      <div className="section-title" style={{ flexWrap: "wrap", gap: 10 }}>
        <h3>Monthly attendance report</h3>
        <div style={{ display: "flex", gap: 8 }}>
          <select value={userId} onChange={(e) => setUserId(e.target.value)} data-testid="hr-report-employee">
            {employees.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.employee_code || u.role}</option>)}
          </select>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="hr-report-month" />
          <button className="outline-button" onClick={download} disabled={!data}>Download CSV</button>
        </div>
      </div>

      {!data ? <Loading>Computing report…</Loading> : (
        <>
          <div className="muted" style={{ marginBottom: 8 }}>
            {data.employee.name} · {data.employee.department || "—"} · {data.employee.designation || "—"} · Working days <strong>{data.working_days}</strong>
          </div>
          <div className="kpi-row" style={{ display: "grid", gap: 8, gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", marginBottom: 10 }}>
            {[["Present", data.totals.present], ["Half-day", data.totals.half_day], ["Leave", data.totals.leave], ["Absent", data.totals.absent], ["Late logins", data.totals.late], ["Total hours", `${data.totals.total_hours}h`]].map(([l, v]) => (
              <div key={l} style={{ padding: 10, border: "1px solid #eee", borderRadius: 8 }}>
                <p className="eyebrow" style={{ margin: 0 }}>{l}</p>
                <strong style={{ fontSize: 18 }}>{v}</strong>
              </div>
            ))}
          </div>
          <table className="records-table">
            <thead><tr>
              <th>Date</th><th>Day</th><th>Status</th><th>Login</th><th>Logout</th><th>Hours</th><th>Late</th><th>Note</th>
            </tr></thead>
            <tbody>
              {data.days.map((d) => (
                <tr key={d.date} data-testid={`hr-report-row-${d.date}`}>
                  <td>{d.date}</td>
                  <td>{d.weekday}</td>
                  <td>{d.status ? <span className={`chip chip-${d.status}`}>{d.status}</span> : "—"}</td>
                  <td>{d.login_time ? d.login_time.slice(11, 16) : "—"}</td>
                  <td>{d.logout_time ? d.logout_time.slice(11, 16) : "—"}</td>
                  <td>{d.working_hours ? `${d.working_hours}h` : "—"}</td>
                  <td>{d.late_login ? <span style={{ color: "#dc2626", fontWeight: 700 }}>YES</span> : "—"}</td>
                  <td className="muted">{d.note || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}

